import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

// Validation helper functions
const isValidDate = (dateStr) => {
  if (!dateStr) return false;
  const date = dayjs(dateStr);
  const today = dayjs();
  return date.isValid() && date.isBefore(today);
};

const isValidAmount = (amount) => {
  const numAmount = parseFloat(amount);
  return !isNaN(numAmount) && numAmount > 0;
};

// Helper function to parse date of service string
const parseDateOfService = (dateStr) => {
  if (!dateStr) return { singleDates: [], dateRanges: [] };

  const dates = dateStr.split(',').map(d => d.trim());
  const singleDates = [];
  const dateRanges = [];

  dates.forEach(date => {
    if (date.includes('-')) {
      // Check if it's a date range (contains more than one hyphen)
      const parts = date.split('-').map(p => p.trim());
      if (parts.length === 3) {
        // Single date with hyphens (MM-DD-YYYY)
        const parsedDate = dayjs(date);
        if (parsedDate.isValid()) {
          singleDates.push(parsedDate.format('YYYY-MM-DD'));
        }
      } else if (parts.length === 2) {
        // Date range
        const startDate = dayjs(parts[0]);
        const endDate = dayjs(parts[1]);
        if (startDate.isValid() && endDate.isValid()) {
          dateRanges.push([
            startDate.format('YYYY-MM-DD'),
            endDate.format('YYYY-MM-DD')
          ]);
        }
      }
    } else {
      // Single date
      const parsedDate = dayjs(date);
      if (parsedDate.isValid()) {
        singleDates.push(parsedDate.format('YYYY-MM-DD'));
      }
    }
  });

  return { singleDates, dateRanges };
};

// Define expected headers and their validations
const EXCEL_HEADERS = {
  'Patient ID': {
    key: 'mrn',
    required: true,
  },
  'Patient Title': {
    key: 'title',
    required: false,
  },
  'Patient First Name': {
    key: 'patient_first_name',
    required: true,
  },
  'Patient Last Name': {
    key: 'patient_last_name',
    required: true,
  },
  'Gender': {
    key: 'gender',
    required: true,
    validate: (value) => ['male', 'female', 'other'].includes(value?.toLowerCase()),
    errorMessage: 'Gender must be Male, Female, or Other'
  },
  'Date of Birth': {
    key: 'date_of_birth',
    required: true,
    validate: isValidDate,
  },
  'Organization': {
    key: 'organization',
    required: true,
  },
  'Region': {
    key: 'region',
    required: true,
  },
  'Hospital': {
    key: 'hospital',
    required: true,
  },
  'Visit Type': {
    key: 'visit_type',
    required: true,
    validate: (value) => ['IP', 'OP', 'ZPO', 'ZURG','ER','AMB','HHD','OBS','DAY','LAB','RAD','PT','DIA','CHEMO','CONS','FUP','WELL','TELE'].includes(value?.toUpperCase()),
    errorMessage: 'Visit type must be IP, OP, ZPO, ZURG, ER, AMB, HHD, OBS, DAY, LAB, RAD, PT, DIA, CHEMO, CONS, FUP, WELL, or TELE'
  },
  'State': {
    key: 'state',
    required: true,
  },
  'Process Type': {
    key: 'process_type',
    required: true,
    validate: (value) => ['ma standard', 'ma expedited'].includes(value?.toLowerCase()),
    errorMessage: 'Process type must be MA Standard or MA Expedited'
  },
  'Revenue Cycle': {
    key: 'revenue_cycle',
    required: true,
    validate: (value) => ['pre service', 'post claim', 'part b drugs'].includes(value?.toLowerCase()),
    errorMessage: 'Revenue cycle must be Pre Service, Post Claim, or Part B Drugs'
  },
  'Revenue Cycle Stage': {
    key: 'revenue_cycle_stage',
    required: true,
    validate: (value) => ['organization determination', 'first appeal', 'second appeal'].includes(value?.toLowerCase()),
    errorMessage: 'Revenue cycle stage must be Organization Determination, First Appeal, or Second Appeal'
  },
  'Allocated To': {
    key: 'allocated_to',
    required: false,
  },
  'Claim Amount': {
    key: 'case_amount',
    required: true,
    validate: isValidAmount,
    errorMessage: 'Claim amount must be a positive number'
  },
  'Date of Service': {
    key: 'date_of_service',
    required: true,
    validate: (value) => {
      if (!value) return false;
      const { singleDates, dateRanges } = parseDateOfService(value);
      // Valid if we have at least one single date or one date range
      return (singleDates.length > 0 || dateRanges.length > 0);
    },
    errorMessage: 'Invalid date of service format. Use MM-DD-YYYY for single dates and MM-DD-YYYY - MM-DD-YYYY for ranges, separated by commas'
  },
  'Admission Date': {
    key: 'admission_date',
    required: false,
    validate: isValidDate,
  },
  'Discharge Date': {
    key: 'discharge_date',
    required: false,
    validate: isValidDate,
  },
  'Hearing Date': {
    key: 'hearing_date',
    required: false,
    validate: isValidDate,
  },
  'Insurance Company': {
    key: 'insurance_company',
    required: true,
  },
  'Insurance Claim Number': {
    key: 'insurance_claim_number',
    required: true,
  },
  'Providers Encounter Number': {
    key: 'providers_encounter_number',
    required: true,
  },
  'EOB Date': {
    key: 'date_of_eob',
    required: true,
    validate: isValidDate,
  },
  'Clinical Summary': {
    key: 'clinical_summary',
    required: false,
  },
  'Clinical Summary Document': {
    key: 'clinical_summary_document',
    required: false,
  },
  'Denial Type': {
    key: 'denial_type',
    required: false,
  },
  'Denial Date': {
    key: 'denial_date',
    required: false,
    validate: isValidDate,
  },
  'Denial Code 1': {
    key: 'denial_code_1',
    required: false,
  },
  'Denial Code 2': {
    key: 'denial_code_2',
    required: false,
  },
  'Rejected Category': {
    key: 'rejected_category',
    required: false,
  },
  'Denial Remarks': {
    key: 'denial_reason',
    required: false,
  },
  'Denial Letter Document': {
    key: 'denial_letter_document',
    required: false,
  },
  'Supporting Document File 1': {
    key: 'supporting_document_file_1',
    required: false,
  },
  'Supporting Document Name 1': {
    key: 'supporting_document_name_1',
    required: false,
  },
  'Supporting Document File 2': {
    key: 'supporting_document_file_2',
    required: false,
  },
  'Supporting Document Name 2': {
    key: 'supporting_document_name_2',
    required: false,
  },  
  'Supporting Document File 3': {
    key: 'supporting_document_file_3',
    required: false,
  },
  'Supporting Document Name 3': {
    key: 'supporting_document_name_3',
    required: false,
  },
  'Supporting Document File 4': {
    key: 'supporting_document_file_4',
    required: false,
  },
  'Supporting Document Name 4': { 
    key: 'supporting_document_name_4',
    required: false,
  },
  'Supporting Document File 5': {
    key: 'supporting_document_file_5',
    required: false,
  },  
  'Supporting Document Name 5': {
    key: 'supporting_document_name_5',
    required: false,
  },  
};


// Main validation function
export const validateExcelData = async (file) => {
  const errors = [];
  const validRows = [];
  const documentNames = new Set(); // Track unique document names
  
  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array', cellDates: true });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    
    // Get headers from first row
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const headers = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })];
      headers[C] = cell?.v || '';
    }

    // Validate headers first
    const missingHeaders = Object.keys(EXCEL_HEADERS).filter(
      requiredHeader => !headers.includes(requiredHeader)
    );

    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
    }

    // Convert to JSON and validate all rows at once
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });

    // Process all rows without showing individual notifications
    jsonData.forEach((row, index) => {
      const rowErrors = [];
      const rowNumber = index + 2; // Adding 2 to account for header row and 0-based index
      const mappedRow = {};

      // Collect document names from document columns
      if (row['Clinical Summary Document']) {
        documentNames.add(row['Clinical Summary Document'].trim());
      }
      if (row['Denial Letter Document']) {
        documentNames.add(row['Denial Letter Document'].trim());
      }
      
      for (let i = 1; i <= 5; i++) {
        const fileField = `Supporting Document File ${i}`;
        if (row[fileField]) {
          documentNames.add(row[fileField].trim());
        }
      }

      // Validate each field based on header configuration
      Object.entries(EXCEL_HEADERS).forEach(([header, config]) => {
        const value = row[header];
        mappedRow[config.key] = value;

        if (config.required && !value) {
          rowErrors.push(`${header} is required`);
        }

        if (config.validate && value) {
          if (!config.validate(value)) {
            rowErrors.push(config.errorMessage || `Invalid ${header}`);
          }
        }
      });

      // Add check for Clinical Summary or Clinical Summary Document
      if (!row['Clinical Summary'] && !row['Clinical Summary Document']) {
        rowErrors.push('Either Clinical Summary or Clinical Summary Document must be provided');
      }

      // Add validation for denial fields when Revenue Cycle Stage is not Organization Determination
      if (row['Revenue Cycle Stage']?.toLowerCase() !== 'organization determination') {
        if (!row['Denial Remarks'] && !row['Denial Letter Document']) {
          rowErrors.push('Either Denial Remarks or Denial Letter Document must be provided for non-Organization Determination stages');
        }
        if (!row['Denial Type']) {
          rowErrors.push('Denial Type is required for non-Organization Determination stages');
        }
        if (!row['Denial Date']) {
          rowErrors.push('Denial Date is required for non-Organization Determination stages');
        }
      }

      // Add validation for Supporting Document Files and Names
      for (let i = 1; i <= 5; i++) {
        const fileField = `Supporting Document File ${i}`;
        const nameField = `Supporting Document Name ${i}`;
        
        if (row[fileField] && !row[nameField]) {
          rowErrors.push(`${nameField} is required when ${fileField} is provided`);
        }
        if (!row[fileField] && row[nameField]) {
          rowErrors.push(`${fileField} is required when ${nameField} is provided`);
        }
      }

      if (rowErrors.length > 0) {
        errors.push({
          row: rowNumber,
          errors: rowErrors
        });
      } else {
        validRows.push(mappedRow);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      validRows,
      totalRows: jsonData.length,
      requiredDocuments: Array.from(documentNames) // Add the collected document names to the results
    };

  } catch (error) {
    console.error('Error validating Excel file:', error);
    throw new Error(error.message || 'Failed to validate Excel file');
  }
};

// Function to format validation results for display
export const formatValidationResults = (validationResults) => {
  const { isValid, errors, validRows, totalRows, requiredDocuments } = validationResults;
  
  return {
    summary: {
      totalRows,
      validRows: validRows.length,
      errorRows: errors.length,
      isValid
    },
    errorDetails: errors.map(error => ({
      rowNumber: error.row,
      errors: error.errors
    })),
    requiredDocuments // Add the required documents to the formatted results
  };
};

// Function to prepare data for batch creation
export const prepareBatchData = (validRows) => {
  return validRows.map(row => {
    const { singleDates, dateRanges } = parseDateOfService(row.date_of_service);
    
    return {
      mrn: row.mrn,
      title: row.title,
      patient_first_name: row.patient_first_name,
      patient_last_name: row.patient_last_name,
      gender: row.gender,
      date_of_birth: dayjs(row.date_of_birth).format('YYYY-MM-DD'),
      organization: row.organization,
      region: row.region,
      hospital: row.hospital,
      visit_type: row.visit_type?.toLowerCase(),
      state: row.state,
      process_type: row.process_type?.toLowerCase(),
      revenue_cycle: row.revenue_cycle?.toLowerCase(),
      revenue_cycle_stage: row.revenue_cycle_stage?.toLowerCase(),
      allocated_to: row.allocated_to,
      case_amount: parseFloat(row.case_amount),
      date_of_service: {
        single_date: singleDates,
        date_range: dateRanges
      },
      admission_date: row.admission_date ? dayjs(row.admission_date).format('YYYY-MM-DD') : null,
      discharge_date: row.discharge_date ? dayjs(row.discharge_date).format('YYYY-MM-DD') : null,
      hearing_date: row.hearing_date ? dayjs(row.hearing_date).format('YYYY-MM-DD') : null,
      insurance_company: row.insurance_company,
      insurance_claim_number: row.insurance_claim_number,
      providers_encounter_number: row.providers_encounter_number,
      date_of_eob: dayjs(row.date_of_eob).format('YYYY-MM-DD'),
      clinical_summary: row.clinical_summary || '',
      clinical_summary_document: row.clinical_summary_document || '',
      denial_type: row.denial_type || '',
      denial_date: row.denial_date ? dayjs(row.denial_date).format('YYYY-MM-DD') : null,
      denial_code_1: row.denial_code_1 || '',
      denial_code_2: row.denial_code_2 || '',
      rejected_category: row.rejected_category || '',
      denial_reason: row.denial_reason || '',
      denial_letter_document: row.denial_letter_document || ''
    };
  });
};